<script setup>
import {
    ref,
    getCurrentInstance
} from 'vue'
import {
    DriverStore
} from '../pinia/Driver.js'
import emitter from '../utils/emitter'

const Driver = DriverStore()
const { proxy } = getCurrentInstance()
const premium = ref(false)
const crew = ref(false)

async function unlinkdiscord() {
    await proxy.$api({
        serviceName: "account",
        methodName: "unlinkdiscord"
    })
    Driver.user.discord = null
    emitter.emit('shell-notify', {
        title: 'Discord',
        message: 'Discord account unlinked.',
        type: 'info',
    })
}

async function getRolePremium() {
    if (premium.value) return
    premium.value = true
    try {
        await getRole('premium')
    } finally {
        premium.value = false
    }
}

async function getRoleCrew() {
    if (crew.value) return
    crew.value = true
    try {
        await getRole('crew')
    } finally {
        crew.value = false
    }
}

async function getRole(role) {
    try {
        const result = await proxy.$api({
            serviceName: "account",
            methodName: "getRole",
            parameters: [role]
        })
        if (result?.ok) {
            emitter.emit('shell-notify', {
                title: 'Discord',
                message: role === 'premium'
                    ? 'Premium role assigned on Discord.'
                    : 'Crew Owner role assigned on Discord.',
                type: 'success',
            })
            return
        }
        emitter.emit('shell-notify', {
            title: 'Discord',
            message: result?.error || 'Could not assign Discord role.',
            type: 'error',
            timeout: 7000,
        })
    } catch (error) {
        emitter.emit('shell-notify', {
            title: 'Discord',
            message: error?.message || 'Could not assign Discord role.',
            type: 'error',
            timeout: 7000,
        })
    }
}
</script>

<template><div class="settings-tab-page discordForm">
    <div class="settings-tab-header">
        <div class="header-icon">
            <v-icon size="28">mdi-discord</v-icon>
        </div>
        <div class="header-content">
            <h3 class="page-title">Discord</h3>
            <p v-if="Driver.user.discord != null" class="page-subtitle">
                Connected as {{ Driver.user.discord_name }}
            </p>
            <p v-else class="page-subtitle">
                Link your Discord account to unlock special features
            </p>
        </div>
    </div>

    <div class="section-list">
        <div class="section-row">
            <div class="section-body">
                <div v-if="Driver.user.discord == null" class="discord-not-connected">
                    <div class="info-box">
                        <v-icon size="64">mdi-link-variant-off</v-icon>
                        <p class="info-text">Your Discord account is not linked yet</p>
                    </div>
                    <a onclick="open('https://api.nightriderz.world/discord-moonset.php?key=' + localStorage.getItem('easharpptr_u'), 'Discord', 'scrollbars=1,resizable=1,height=700,width=500')">
                        <v-btn variant="tonal" color="primary" size="large">
                            <v-icon class="mr-2">mdi-discord</v-icon>
                            Login with Discord
                        </v-btn>
                    </a>
                </div>

                <div v-else>
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-account-cog</v-icon>
                                Discord Account
                            </div>
                            <div class="setting-description">
                                Your linked Discord account
                            </div>
                            <v-text-field prepend-icon="mdi-discord" v-model="Driver.user.discord_name" label="Discord" variant="outlined" disabled density="comfortable" hide-details class="setting-input"></v-text-field>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-shield-star</v-icon>
                                Discord Roles
                            </div>
                            <div class="setting-description">
                                Claim your roles on Discord. You must already be in the NightRiderz Discord server.
                            </div>
                            <div class="roles-container">
                                <v-btn v-if="Driver.user.premium" :loading="premium" :disabled="premium" @click="getRolePremium" variant="tonal" class="role-btn">
                                    <span class="role-dot premium-dot">●</span> Premium
                                </v-btn>
                                <v-btn v-if="Driver.currentCrew && Driver.currentCrew.ownerUserId == Driver.persona.ID" :loading="crew" :disabled="crew" @click="getRoleCrew" variant="tonal" class="role-btn">
                                    <span class="role-dot crew-dot">●</span> Crew Owner
                                </v-btn>
                            </div>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-link-variant-off</v-icon>
                                Manage Connection
                            </div>
                            <div class="setting-description">
                                Remove the link between your Discord and NightRiderz accounts
                            </div>
                            <v-btn @click="unlinkdiscord" variant="tonal" color="error">
                                <v-icon class="mr-2">mdi-link-variant-off</v-icon>
                                Unlink my account
                            </v-btn>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div></template>

<style scoped>
.discord-not-connected {
  text-align:center}
.info-box {
  padding:30px;
  margin-bottom:20px}
.info-box .v-icon {
  color:var(--faint)}
.info-text {
  font-size:.95em;
  color:var(--mute);
  margin:0}
.roles-container {
  display:flex;
  gap:12px;
  flex-wrap:wrap}
.role-btn {
  flex:1;
  min-width:200px}
.role-dot {
  font-size:1.5rem;
  margin-right:8px;
  line-height:1}
.premium-dot {
  color:#f0ab00}
.crew-dot {
  color:#3ba370}
</style>
